/**
 * Which organization Activist staff are viewing read-only, if any.
 *
 * Here beside `organizationScope` rather than inside React, because the API
 * layer has to ask it too: the request interceptor in `client.ts` refuses
 * every write while a view is on, and it runs far away from any component.
 * `ViewingAsProvider` is what sets and clears it.
 *
 * Kept in sessionStorage, so the view survives a reload in this tab and goes
 * when the tab does, the same as the session itself.
 */
const KEY = 'activistarc.portal.viewing';

export interface ViewedOrganization {
  id: string;
  name: string;
}

function isViewed(value: unknown): value is ViewedOrganization {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ViewedOrganization).id === 'string' &&
    typeof (value as ViewedOrganization).name === 'string'
  );
}

export const viewingScope = {
  get(): ViewedOrganization | null {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      // Anything else in the slot is treated as no view at all, rather than
      // as a view of an organization called "undefined".
      return isViewed(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  set(organization: ViewedOrganization | null): void {
    try {
      if (organization) sessionStorage.setItem(KEY, JSON.stringify(organization));
      else sessionStorage.removeItem(KEY);
    } catch {
      // Private windows and blocked site data both throw. The view then lasts
      // only until a reload, which is the safe way round.
    }
  },

  clear(): void {
    this.set(null);
  },
};
